import {type FormEvent, useState} from "react";
import {BaseAlert, BaseButton, BaseCard, BaseInput, BaseSelect} from "@/components";
import {BANK_OPTIONS} from "./banks";
import {type DepositPagoMovilFormInput, depositPagoMovilSchema} from "./depositPagoMovil.schema";
import {useDepositPagoMovil} from "./useDepositPagoMovil";

type FieldErrors = Partial<Record<keyof DepositPagoMovilFormInput, string>>;

const todayISO = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const INITIAL_FORM: DepositPagoMovilFormInput = {
  referencia: "",
  importe: "" as unknown as number,
  fecha: todayISO(),
  banco_origen: "",
};

export interface DepositPagoMovilPageProps {
  onBack?: () => void;
  onSuccess?: () => void;
}

const DepositPagoMovilPage = ({onBack, onSuccess}: DepositPagoMovilPageProps) => {
  const [form, setForm] = useState<DepositPagoMovilFormInput>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const {submit, isPending, isSuccess, isError, error, reset} = useDepositPagoMovil();

  const updateField = <K extends keyof DepositPagoMovilFormInput>(key: K, value: DepositPagoMovilFormInput[K]) => {
    setForm(prev => ({...prev, [key]: value}));
    setErrors(prev => ({...prev, [key]: undefined}));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = depositPagoMovilSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof DepositPagoMovilFormInput | undefined;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    submit(parsed.data);
  };

  const handleNewDeposit = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    reset();
  };

  if (isSuccess) {
    return (
      <section className="mx-auto flex w-full max-w-lg flex-col gap-4 px-4 py-8">
        <BaseAlert
          variant="success"
          title="Depósito reportado"
        >
          <p className="mb-3">Recibimos tu reporte. Verificaremos el pago y acreditaremos el saldo en breve.</p>
          <div className="flex flex-wrap gap-2">
            <BaseButton
              variant="primary"
              size="sm"
              onClick={handleNewDeposit}
            >
              Reportar otro depósito
            </BaseButton>
            {onSuccess ? (
              <BaseButton
                variant="secondary"
                size="sm"
                onClick={onSuccess}
              >
                Volver a la billetera
              </BaseButton>
            ) : null}
          </div>
        </BaseAlert>
      </section>
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-lg flex-col gap-6 px-4 py-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Depósito por Pago Móvil</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Reporta tu transferencia para acreditar saldo en tu billetera.
        </p>
      </header>

      {isError ? (
        <BaseAlert
          variant="danger"
          title="No pudimos registrar tu depósito"
        >
          <p>{error?.message ?? "Verifica los datos e inténtalo nuevamente."}</p>
        </BaseAlert>
      ) : null}

      <BaseCard
        variant="surface"
        padding="lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
        >
          <BaseSelect
            label="Banco de origen"
            placeholder="Selecciona un banco"
            options={BANK_OPTIONS}
            value={form.banco_origen}
            onChange={e => updateField("banco_origen", e.target.value)}
            error={errors.banco_origen}
            disabled={isPending}
          />

          <BaseInput
            label="Número de referencia"
            inputMode="numeric"
            autoComplete="off"
            placeholder="Últimos dígitos de la referencia"
            value={form.referencia}
            onChange={e => updateField("referencia", e.target.value)}
            error={errors.referencia}
            disabled={isPending}
          />

          <BaseInput
            label="Monto (Bs.)"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={String(form.importe ?? "")}
            onChange={e => updateField("importe", e.target.value as unknown as number)}
            error={errors.importe}
            disabled={isPending}
          />

          <BaseInput
            label="Fecha del depósito"
            type="date"
            value={form.fecha}
            max={todayISO()}
            onChange={e => updateField("fecha", e.target.value)}
            error={errors.fecha}
            disabled={isPending}
          />

          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <BaseButton
              type="submit"
              variant="primary"
              disabled={isPending}
              fullWidth
            >
              {isPending ? "Enviando..." : "Reportar depósito"}
            </BaseButton>
            {onBack ? (
              <BaseButton
                type="button"
                variant="ghost"
                onClick={onBack}
                disabled={isPending}
              >
                Cancelar
              </BaseButton>
            ) : null}
          </div>
        </form>
      </BaseCard>
    </section>
  );
};

export default DepositPagoMovilPage;
